import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight,
  FileText,
  Instagram,
  Mail,
  Search,
  Film,
  Loader2,
  Copy,
  Sparkles,
  Calendar,
} from "lucide-react";

const TOOLS = [
  {
    id: "blog_ideas",
    slug: "blog",
    label: "Blog idea generator",
    desc: "5 clickable headline ideas for any topic.",
    placeholder: "e.g. AI for solo founders in 2026",
    icon: FileText,
  },
  {
    id: "social_caption",
    slug: "social",
    label: "Social caption generator",
    desc: "3 warm, human captions ready to post.",
    placeholder: "e.g. Launching a mobile detailing biz in LA",
    icon: Instagram,
  },
  {
    id: "email_subject",
    slug: "email",
    label: "Marketing email subject",
    desc: "5 subject lines that earn opens.",
    placeholder: "e.g. Announcing the founding member pass",
    icon: Mail,
  },
  {
    id: "seo_meta",
    slug: "seo",
    label: "SEO meta generator",
    desc: "Title + meta description + keywords + outline.",
    placeholder: "e.g. best editorial AI content studio",
    icon: Search,
  },
  {
    id: "video_prompt",
    slug: "video",
    label: "Video prompt generator",
    desc: "3 cinematic 5-second video prompts.",
    placeholder: "e.g. a candle-lit dinner in Tokyo, 35mm",
    icon: Film,
  },
];

export default function Tools() {
  const [active, setActive] = useState(TOOLS[0]);
  const [topic, setTopic] = useState("");
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 3, remaining: 3 });

  useEffect(() => {
    // Sync from URL hash
    const hash = window.location.hash.replace("#", "");
    const t = TOOLS.find((x) => x.slug === hash);
    if (t) setActive(t);
    api.get("/public/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  async function handleRun() {
    if (!topic.trim()) {
      toast.error("Give the tool a topic.");
      return;
    }
    setBusy(true);
    setOutput(null);
    try {
      const r = await api.post("/public/tool", { tool: active.id, topic });
      setOutput(r.data.output);
      const q = await api.get("/public/quota");
      setQuota(q.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  }

  function copyOut() {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast.success("Copied");
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tools-page">
      <MarketingNav />

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto p-8 md:p-14">
          <div className="overline text-muted-foreground">Free tools · No signup</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none tracking-tight mt-4">
            Pocket-sized
            <br />
            <span className="italic text-primary">generators.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-muted-foreground">
            Six free generators. No account. Three runs per day per browser. When you're ready to
            publish a whole week — sign up and open the workspace.
          </p>
          <div className="mt-6 overline text-[10px] text-muted-foreground" data-testid="quota-line">
            {quota.remaining} of {quota.limit} free runs left today
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          {/* Tool picker */}
          <aside className="lg:col-span-4 border-r border-border">
            <Link
              to="/free-week"
              className="p-6 md:p-8 border-b border-border block bg-foreground text-background hover:bg-primary hover:text-primary-foreground transition-colors group"
              data-testid="free-week-cta"
            >
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5" />
                <span className="overline text-[10px]">The crown jewel · Free</span>
              </div>
              <div className="font-serif-display text-2xl md:text-3xl mt-4 leading-tight">
                Free Week of Content Generator
              </div>
              <div className="mt-3 text-xs opacity-80">
                Give us one idea → get seven days of content →{" "}
                <span className="group-hover:text-primary-foreground text-primary">open Draftwave</span>
              </div>
              <ArrowRight className="w-5 h-5 mt-4 opacity-60 group-hover:translate-x-1 transition-transform" />
            </Link>
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const on = active.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    setActive(t);
                    setOutput(null);
                    setTopic("");
                    window.history.replaceState(null, "", `#${t.slug}`);
                  }}
                  className={`w-full text-left p-5 md:p-6 border-b border-border transition-colors ${
                    on ? "bg-foreground text-background" : "hover:bg-muted"
                  }`}
                  data-testid={`tools-picker-${t.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="overline opacity-60 text-[10px]">Free</span>
                  </div>
                  <div className="mt-3 font-medium">{t.label}</div>
                  <div className="text-xs opacity-70 mt-1">{t.desc}</div>
                </button>
              );
            })}
          </aside>

          {/* Runner */}
          <div className="lg:col-span-8 p-6 md:p-12">
            <div className="overline text-muted-foreground">Runner</div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-none mt-3">
              {active.label}
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">{active.desc}</p>

            <div className="mt-8">
              <label className="overline text-muted-foreground">Topic</label>
              <div className="mt-2 flex flex-col md:flex-row gap-2">
                <Input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={active.placeholder}
                  className="rounded-none h-12 font-mono text-sm flex-1"
                  data-testid="tools-topic"
                  onKeyDown={(e) => e.key === "Enter" && handleRun()}
                />
                <Button
                  onClick={handleRun}
                  disabled={busy}
                  className="rounded-none h-12 px-6 font-mono uppercase tracking-widest text-xs"
                  data-testid="tools-run-btn"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Generate
                    </>
                  )}
                </Button>
              </div>
            </div>

            {output && (
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <div className="overline text-muted-foreground">Output</div>
                  <Button
                    onClick={copyOut}
                    variant="outline"
                    size="sm"
                    className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest"
                    data-testid="tools-copy-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
                <pre
                  className="mt-3 whitespace-pre-wrap font-mono text-sm leading-relaxed bg-background border border-border p-5 max-h-[60vh] overflow-auto"
                  data-testid="tools-output"
                >
                  {output}
                </pre>
                <div className="mt-6 p-5 border border-primary bg-primary/5 flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-1">
                    <div className="font-serif-display text-2xl leading-tight">
                      Want to generate the entire campaign?
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Open Draftwave — 120 free credits, no credit card.
                    </div>
                  </div>
                  <Link to="/login" data-testid="tools-upsell-cta">
                    <Button className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs">
                      Open Draftwave <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {!output && !busy && (
              <div className="mt-8 border border-dashed border-border p-10 text-center text-muted-foreground text-sm" data-testid="tools-empty">
                Type a topic. Hit generate. Copy. Ship.
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
